import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, catchError, delay, map, of, switchMap } from "rxjs";
import { Answer, BACKEND_QUESTION_TYPES, BackendQuestion, Question, backendTypeToComponentType } from "../classes/dynamic-form";
import { Environment } from "../classes/environment";
import { MatDialog } from "@angular/material/dialog";
import { InfoDialogComponent } from "@trivelope/triv-ui-tools-lib";
import { EventCategory, PortalEventBackend } from "../classes/calendar";


@Injectable( { providedIn: 'root' } )
export class QuestionService {


	constructor(
		private httpClient: HttpClient,
		@Inject( "environment" ) private environment: Environment,
		private matDialog: MatDialog,
	){}


	getQuestionsOfCategory( categoryId: string ): Observable<Question[]> {

		return this.httpClient.get<BackendQuestion[]>(
			this.environment.backendUrl + 'getFragenByFragenKategorie/' + categoryId
		).pipe(
			map( questions => {

				if( !questions || !questions.length ) return [];

				questions.sort( (a,b) => {
					if( a.anlageDatum === b.anlageDatum ) return 0;
					if( (a.anlageDatum === null) || (a.anlageDatum === undefined) ) return -1;
					if( (b.anlageDatum === null) || (b.anlageDatum === undefined) ) return 1;
					return a.anlageDatum - b.anlageDatum;
				});

				return questions.map( q => this.convertQuestionBackendToFrontend(q) );
			} ),
			catchError( _err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: "Fehler", message: "Beim Abrufen des Formulars ist ein Fehler aufgetreten" }
				});
				return of([]);
			})
		);

	}

	private convertQuestionBackendToFrontend( backendBranch: BackendQuestion ): Question {

		const q = new Question();
		q.id = backendBranch.frageId;
		q.label = backendBranch.frage;
		q.required = backendBranch.pflichtfrage;
		q.childrenCondition = backendBranch.bedingung;
		q.parentId = backendBranch.hauptfrageId;
		q.excludesQuestionId = backendBranch.ausschlussFragen;
		q.sequenceNr = +backendBranch.reihenfolge;
		q.deregistration = backendBranch.abmeldung;

		q.bIsLoop = ( backendBranch.fragenTyp.bezeichnung == "MehrwachUnterfragen" );

		const strType = backendTypeToComponentType[ backendBranch.fragenTyp.bezeichnung ];
		if( strType ) {
			q.type = strType;
		}
		else {
			console.log( "!! convertQuestionBackendToFrontendAndInit() unknown type:", backendBranch.fragenTyp, " for branch ", backendBranch.frageId )
			q.type = "text";
		}

		if( backendBranch.fragenTyp.bezeichnung == "MehrwachUnterfragen" ) q.bIsLoop = true;


		// the question is visible only if it has no parent, at first
		q.bIsVisible = !q.parentId;

		return q;

	}



	saveAnswers( answer: Answer ): Observable<boolean> {

		return this.httpClient.post<Answer>(
			this.environment.backendUrl + 'veranstaltungKategorie/saveVeranstaltungKategorie',
			answer
		).pipe(
			map( response => {
				if( !response || !response.vbId ) return false;
				return true;
			} ),
			catchError( _err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: "Fehler", message: "Beim Sprechen des Formulars ist ein Fehler aufgetreten" }
				});
				return of(false);
			})
		);

	}

	getAnswer( userId: string, eventId: string ): Observable<Answer | null> {

		return this.httpClient.get<{
			ebeId: string,
			vbAngemeldet: boolean,
			vbAntwort: string,
			vbId: string,
			veranstaltung: PortalEventBackend
		}[]>(
			this.environment.backendUrl + 'allVeranstaltungKategorie',
		).pipe(
			map( answers => {

				if( !answers || !answers.length ) return null;

				const filtered = answers.filter( a => (a.ebeId == userId) && (a.veranstaltung && a.veranstaltung.veranstaltungId == eventId) );
				if( !filtered || !filtered.length ) return null;
				if( filtered.length > 1 ) {
					console.log( "MULTIPLE ANSWERS FOR THIS USER AND EVENT?!", filtered );
				}

				const answer = new Answer();
				answer.ebeId = filtered[0].ebeId;
				answer.vbAngemeldet = filtered[0].vbAngemeldet;
				answer.vbAntwort = filtered[0].vbAntwort;
				answer.vbId = filtered[0].vbId;
				answer.veranstaltungId = filtered[0].veranstaltung.veranstaltungId;

				return answer;
			} ),
			catchError( _err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: "Fehler", message: "Beim Abrufen des Formulars ist ein Fehler aufgetreten" }
				});
				return of(null);
			})
		);

	}

  getAnswerForVeranstaltung( userId: string, eventId: string ): Observable<Answer | null> {
    // it gets a single answer for a single event
    return this.httpClient.get<{
      ebeId: string,
      vbAngemeldet: boolean,
      vbAntwort: string,
      vbId: string,
      veranstaltung: PortalEventBackend
    }>(
      this.environment.backendUrl + 'veranstaltungKategorie/' + userId + '/' + eventId,
    ).pipe(
      map( answer => {
        if( !answer ) return null;
        const a = new Answer();
        a.ebeId = answer.ebeId;
        a.vbAngemeldet = answer.vbAngemeldet;
        a.vbAntwort = answer.vbAntwort;
        a.vbId = answer.vbId;
        a.veranstaltungId = answer.veranstaltung.veranstaltungId;
        return a;
      }
      ),
      catchError( _err => {
        this.matDialog.open( InfoDialogComponent, {
          data: { title: "Fehler", message: "Beim Abrufen des Formulars ist ein Fehler aufgetreten" }
        });
        return of(null);
      }
      )
    );

  }


  	saveForm( title: string, id: string ): Observable<string|null> {

		const obs = id
			? this.httpClient.put<{ verarbeitet: boolean, fragenkategorien: { fkId: string }}>(
				this.environment.backendUrl + 'fragenkategorie/update',
				{ fkId: id, fkBezeichnung: title }
			)
			: this.httpClient.post<{ verarbeitet: boolean, fragenkategorien: { fkId: string }}>(
				this.environment.backendUrl + 'fragenkategorie/new',
				{ fkBezeichnung: title }
			);

		return obs.pipe(
			map( result => {
				if( !result || !result.verarbeitet ) return null;
				return result.fragenkategorien.fkId;
			} ),
			catchError( e => of(null) )
		);

	}

	getForm( id: string ): Observable<EventCategory & { questions: Question[] }|null> {

		return this.httpClient.get<EventCategory & { fragen: BackendQuestion[] }>(
			this.environment.backendUrl + 'fragenkategorie/' + id
		).pipe(
			map( result => {

				if( !result ) return null;

				const questions = result.fragen ? result.fragen : [];

				questions.sort( (a,b) => {
					if( a.anlageDatum === b.anlageDatum ) return 0;
					if( (a.anlageDatum === null) || (a.anlageDatum === undefined) ) return -1;
					if( (b.anlageDatum === null) || (b.anlageDatum === undefined) ) return 1;
					return a.anlageDatum - b.anlageDatum;
				});

				return {
					...result,
					questions: questions.map( q => this.convertQuestionBackendToFrontend(q) )
				};

			} ),
			catchError( e => {
				console.log( "QUESTION.SERVICE getForm() exception", e )
				return of(null);
			})
		);

	}

	createQuestion( question: Question ): Observable<string> {

		return this.httpClient.post<{ verarbeitet: boolean, frage: BackendQuestion }>(
			this.environment.backendUrl + 'fragen/new',
			this.convertQuestionFrontendToBackend( question )
		).pipe(
			map( result => {
				if( !result || !result.verarbeitet ) return "";
				return result.frage.frageId;
			} ),
			catchError( e => of("") )
		);

	}

	updateQuestion( question: Question ): Observable<boolean|null> {

		return this.httpClient.put<{ verarbeitet: boolean, frage: BackendQuestion }>(
			this.environment.backendUrl + 'fragen/update',
			this.convertQuestionFrontendToBackend( question )
		).pipe(
			map( result => {
				if( !result || !result.verarbeitet ) return null;
				return true;
			} ),
			catchError( e => of(null) )
		);

	}

	private convertQuestionFrontendToBackend( question: Question ): BackendQuestion {

		const backendQuestion = new BackendQuestion();
		backendQuestion.frageId = question.id;
		backendQuestion.frage = question.label;
		backendQuestion.pflichtfrage = question.required;
		backendQuestion.bedingung = question.childrenCondition;
		backendQuestion.hauptfrageId = question.parentId;
		backendQuestion.ausschlussFragen = question.excludesQuestionId;
		backendQuestion.reihenfolge = "" + question.sequenceNr;
		backendQuestion.abmeldung = question.deregistration;

		if( question.bIsLoop ) {
			const backendType = BACKEND_QUESTION_TYPES.find( t => t.ft_bezeichnung == "MehrwachUnterfragen" );
			if( !backendType ) {
				console.warn( "backendType NOT FOUND", BACKEND_QUESTION_TYPES );
			}
			else {
				backendQuestion.fragenTyp.bezeichnung = backendType.ft_bezeichnung;
				backendQuestion.fragenTyp.id = backendType.ft_id;
				backendQuestion.fragenTyp.component = backendType.ft_component;
				backendQuestion.ftId = backendType.ft_id;
			}
		}
		else {

			let bFound = false;
			for( const p in backendTypeToComponentType ) {
				if( !backendTypeToComponentType.hasOwnProperty(p) ) continue;
				if( backendTypeToComponentType[p] == question.type ) {

					const description = p;
					const backendType = BACKEND_QUESTION_TYPES.find( t => t.ft_bezeichnung == description );
					if( !backendType ) {
						console.warn( "backendType NOT FOUND", BACKEND_QUESTION_TYPES );
					}
					else {
						backendQuestion.fragenTyp.bezeichnung = backendType.ft_bezeichnung;
						backendQuestion.fragenTyp.id = backendType.ft_id;
						backendQuestion.fragenTyp.component = backendType.ft_component;
						backendQuestion.ftId = backendType.ft_id;
						bFound = true;
					}
					break;
				}
			}
			if( !bFound ) {
				console.warn( "Question type not found. Invalid?", question.type );
			}

		}

		for( let child of question.children ) {
			backendQuestion.fragen.push( this.convertQuestionFrontendToBackend( child ) );
		}

		return backendQuestion;

	}


	assignQuestionToForm( question: Question, formId: string ): Observable<boolean> {

		if( !question.id ) {
			console.warn( "invalid questionId", question.id, question );
			return of(false);
		}
		if( !formId ) {
			console.warn( "invalid formId", formId );
			return of(false);
		}

		return this.httpClient.post<{ verarbeitet: boolean }>(
			this.environment.backendUrl + 'fragenkategorie/assign/frage',
			{ frId: question.id, fkId: formId, frReihenfolge: question.nInLoop }
		).pipe(
			map( result => (result && result.verarbeitet) ),
			catchError( e => of(false) )
		);

	}

	unassignQuestionFromForm( questionId: string, formId: string ): Observable<boolean> {

		return this.httpClient.delete<{ verarbeitet: boolean }>(
			this.environment.backendUrl + "fragenkategorie/delete/frage",
			{ body: {
				frId: questionId,
				fkId: formId,
			} }
		).pipe(
			map( result => result && result.verarbeitet ),
			catchError( e => of(false) )
		);

	}

	deleteQuestion( questionId: string ): Observable<boolean> {

		return this.httpClient.delete<{ verarbeitet: boolean }>(
			this.environment.backendUrl + "fragen/delete",
			{ params: new HttpParams().set( "frageId", questionId ) }
		).pipe(
			map( result => result && result.verarbeitet ),
			catchError( e => of(false) )
		);

	}

	deleteForm( formId: string ): Observable<boolean> {

		return this.httpClient.delete<{ verarbeitet: boolean }>(
			this.environment.backendUrl + "fragenkategorie/delete",
			{ params: new HttpParams().set( "fkId", formId ) }
		).pipe(
			map( result => result && result.verarbeitet ),
			catchError( e => of(false) )
		);

	}

	isFormDeletable( formId: string ): Observable<boolean|null> {

		return this.httpClient.get<PortalEventBackend[]>(
			this.environment.backendUrl + 'fragenkategorie/events/' + formId
		).pipe(
			map( events => {
				if( !events ) return null;
				return !events.length;
			} ),
			catchError( _err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: "Fehler", message: "Beim Überprüfen des Formulars ist ein Fehler aufgetreten" }
				});
				return of(null);
			})
		);

	}

}
